import { ClipboardEvent, DragEvent, ReactNode, useRef, useState } from 'react';
import { File as FileIcon, ImageIcon, Loader2, Plus, Upload, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { useReportFileUrls } from '@/frontend/reports/primitives';
import { FileRef, WaveformInput } from '@/reports/inputs';
import { RunTrace } from '@/reports/runs';
import { useFill } from './fillContext';

const IMAGE_ACCEPT = 'image/*';
const isImage = (file: File) => file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.name);

/** Upload files through the fill page, reporting how far along it is. Files that fail are dropped with a message. */
function useUploads() {
  const { upload } = useFill();
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (files: File[]): Promise<FileRef[]> => {
    setError(null);
    const done: FileRef[] = [];
    for (const [i, file] of files.entries()) {
      setProgress(Math.round((i / files.length) * 100));
      const ref = await upload(file, (p) => setProgress(Math.round(((i + p / 100) / files.length) * 100)));
      if (ref) done.push(ref);
      else setError(I18n.get('rv2UploadFailed', { file: file.name }));
    }
    setProgress(null);
    return done;
  };
  return { run, progress, error };
}

/** Where files are dropped, pasted or browsed for. */
function DropZone({ accept, multiple, onFiles, busy, children, className, id }: { accept?: string; multiple?: boolean; onFiles: (files: File[]) => void; busy?: boolean; children: ReactNode; className?: string; id?: string }) {
  const browse = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const take = (list: FileList | null | undefined) => {
    const files = Array.from(list ?? []);
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <div
      id={id}
      role="button"
      tabIndex={0}
      aria-busy={busy || undefined}
      className={cn(
        'flex cursor-pointer items-center justify-center gap-2 rounded-lg border-[1px] border-dashed border-input bg-muted/30 text-sm text-muted-foreground transition-colors outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50',
        over && 'border-ring bg-sky-50',
        className,
      )}
      onClick={() => browse.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); browse.current?.click(); } }}
      onDragOver={(e: DragEvent) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e: DragEvent) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
      onPaste={(e: ClipboardEvent) => take(e.clipboardData.files)}
    >
      <input ref={browse} type="file" hidden accept={accept} multiple={multiple} onChange={(e) => { take(e.target.files); e.target.value = ''; }} />
      {children}
    </div>
  );
}

function Busy({ progress }: { progress: number }) {
  return <span className="inline-flex items-center gap-1.5"><Loader2 className="size-4 animate-spin" /> {I18n.get('rv2Uploading', { percent: String(progress) })}</span>;
}

/** Images as thumbnails, with a tile to add more (or one image, replaced by dropping another). */
export function ImageControl({ id, value, multiple, onChange }: { id?: string; value: FileRef[]; multiple?: boolean; onChange: (value: FileRef[]) => void }) {
  const { editable } = useFill();
  const { urls } = useReportFileUrls(value.map((f) => f.fileId));
  const { run, progress, error } = useUploads();
  const add = async (files: File[]) => {
    const images = files.filter(isImage);
    const done = await run(images);
    if (done.length) onChange(multiple ? [...value, ...done] : done.slice(0, 1));
  };
  const showAdd = editable && (multiple || value.length === 0);
  return (
    <div className="flex flex-col gap-1.5">
      {value.length === 0 && !multiple ? (
        editable ? (
          <DropZone id={id} accept={IMAGE_ACCEPT} onFiles={(f) => void add(f)} busy={progress !== null} className="h-24">
            {progress !== null ? <Busy progress={progress} /> : <><Upload className="size-4" /> {I18n.get('rv2DropImage')}</>}
          </DropZone>
        ) : <p className="text-sm text-muted-foreground">{I18n.get('rv2NoImage')}</p>
      ) : (
        <div className="flex flex-wrap gap-[0.75rem]">
          {value.map((f) => (
            <figure key={f.fileId} className="group relative m-0 flex w-32 flex-col gap-1">
              <div className="flex h-24 w-32 items-center justify-center overflow-hidden rounded-lg bg-muted">
                {urls[f.fileId] ? <img src={urls[f.fileId]} alt={f.fileName} className="size-full object-cover" /> : <ImageIcon className="size-6 text-muted-foreground" />}
              </div>
              <figcaption className="truncate text-center text-xs text-muted-foreground" title={f.fileName}>{f.fileName}</figcaption>
              {editable && (
                <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: f.fileName })} onClick={() => onChange(value.filter((x) => x.fileId !== f.fileId))}
                  className="absolute top-1 right-1 flex size-6 items-center justify-center rounded-md bg-background/90 text-muted-foreground shadow-sm hover:text-foreground">
                  <X className="size-3.5" />
                </button>
              )}
            </figure>
          ))}
          {showAdd && (
            <DropZone id={id} accept={IMAGE_ACCEPT} multiple={multiple} onFiles={(f) => void add(f)} busy={progress !== null} className="h-24 w-32 flex-col gap-1 text-xs">
              {progress !== null ? <Busy progress={progress} /> : <><Plus className="size-4" /> {I18n.get('rv2AddPhoto')}</>}
            </DropZone>
          )}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Any file (or those with the extensions given), listed by name. */
export function FileControl({ id, value, multiple, accept, onChange, compact }: { id?: string; value: FileRef[]; multiple?: boolean; accept?: string[]; onChange: (value: FileRef[]) => void; compact?: boolean }) {
  const { editable } = useFill();
  const { urls } = useReportFileUrls(value.map((f) => f.fileId));
  const { run, progress, error } = useUploads();
  const add = async (files: File[]) => {
    const wanted = accept?.length ? files.filter((f) => accept.some((ext) => f.name.toLowerCase().endsWith(ext.toLowerCase()))) : files;
    const done = await run(wanted);
    if (done.length) onChange(multiple ? [...value, ...done] : done.slice(0, 1));
  };
  return (
    <div className="flex flex-col gap-1.5">
      {value.map((f) => (
        <div key={f.fileId} className={cn('flex min-w-0 items-center gap-2 rounded-md border-[1px] px-2.5', compact ? 'h-7' : 'h-8')}>
          <FileIcon className="size-4 shrink-0 text-muted-foreground" />
          {urls[f.fileId] ? <a href={urls[f.fileId]} target="_blank" rel="noreferrer" className="truncate text-sm font-medium">{f.fileName}</a> : <span className="truncate text-sm font-medium">{f.fileName}</span>}
          {editable && (
            <button type="button" className="ml-auto flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label={I18n.get('rv2RemoveItem', { item: f.fileName })} onClick={() => onChange(value.filter((x) => x.fileId !== f.fileId))}>
              <X className="size-3.5" />
            </button>
          )}
        </div>
      ))}
      {editable && (multiple || value.length === 0) && (
        <DropZone id={id} accept={accept?.join(',')} multiple={multiple} onFiles={(f) => void add(f)} busy={progress !== null} className={compact ? 'h-7 text-xs' : 'h-14'}>
          {progress !== null ? <Busy progress={progress} /> : <><Upload className="size-4" /> {compact ? I18n.get('rv2DropFileShort') : I18n.get('rv2DropFile', { types: accept?.join(', ') ?? I18n.get('rv2AnyFile') })}</>}
        </DropZone>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** A small line drawing of a trace's envelope. */
export function Sparkline({ trace, className }: { trace: RunTrace; className?: string }) {
  const points = trace.points;
  if (points.length < 2) return null;
  const xs = points.map((p) => p[0]);
  const ys = points.flatMap((p) => [p[1], p[2]]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const sx = (x: number) => ((x - x0) / (x1 - x0 || 1)) * 240;
  const sy = (y: number) => 60 - ((y - y0) / (y1 - y0 || 1)) * 56 - 2;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${sx(p[0]).toFixed(1)},${sy(p[2]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 240 60" preserveAspectRatio="none" className={cn('h-16 w-60', className)} aria-hidden>
      <line x1="0" x2="240" y1={sy(0)} y2={sy(0)} className="stroke-border" strokeWidth="1" />
      <path d={d} fill="none" className="stroke-sky-700" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const fmt = (n: number) => (Math.abs(n) >= 100 || n === 0 ? String(Math.round(n * 100) / 100) : Number(n.toPrecision(3)).toString());

/** What a capture comes to: points, the x range, and its peak. */
export function traceSummary(trace: RunTrace): string {
  if (trace.error) return trace.error;
  const xs = trace.points.map((p) => p[0]);
  let peak = trace.points[0];
  for (const p of trace.points) if (p[2] > peak[2]) peak = p;
  return I18n.get('rv2TraceSummary', {
    samples: trace.samples.toLocaleString(),
    from: fmt(Math.min(...xs)),
    to: fmt(Math.max(...xs)),
    peak: fmt(peak[2]),
    at: fmt(peak[0]),
  });
}

/** What a capture's columns should be, as its designer named them: "Columns: Time (s), Current (A)". Nothing when unnamed. */
export function captureColumns(input: Pick<WaveformInput, 'xLabel' | 'yLabel'>): string | undefined {
  if (!input.xLabel && !input.yLabel) return undefined;
  return I18n.get('rv2CaptureColumns', { x: input.xLabel || 'x', y: input.yLabel || 'y' });
}

/** A capture: dropped as a CSV or scope export, shown as the server read it. */
export function WaveformControl({ id, input, value, trace: keyed, onChange, compact }: { id?: string; input: WaveformInput; value: FileRef | null; trace?: RunTrace; onChange: (value: FileRef | null) => void; compact?: boolean }) {
  const { editable } = useFill();
  // only the trace read from this file: a key can name another file for a moment, e.g. a table row's after the row above is removed
  const trace = keyed && value && keyed.fileId === value.fileId ? keyed : undefined;
  const { run, progress, error } = useUploads();
  const add = async (files: File[]) => {
    const [done] = await run(files.slice(0, 1));
    if (done) onChange(done);
  };
  if (!value) {
    if (!editable) return <p className="text-sm text-muted-foreground">{I18n.get('rv2NoCapture')}</p>;
    return (
      <div className="flex flex-col gap-1.5">
        <DropZone id={id} accept=".csv,.txt,.tsv,.dat" onFiles={(f) => void add(f)} busy={progress !== null} className={compact ? 'h-7 text-xs' : 'h-20'}>
          {progress !== null ? <Busy progress={progress} /> : <><Upload className="size-4" /> {compact ? I18n.get('rv2DropCsvShort') : I18n.get('rv2DropCsv')}</>}
        </DropZone>
        {!compact && captureColumns(input) && <p className="text-xs text-muted-foreground">{captureColumns(input)}</p>}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }
  if (compact) {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        {trace && !trace.error ? <Sparkline trace={trace} className="h-6 w-16 shrink-0" /> : null}
        <span className="truncate text-sm font-medium" title={value.fileName}>{value.fileName}</span>
        {editable && <button type="button" className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label={I18n.get('rv2RemoveItem', { item: value.fileName })} onClick={() => onChange(null)}><X className="size-3" /></button>}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-[1rem] rounded-lg border-[1px] px-[0.75rem] py-2">
      {trace && !trace.error ? <Sparkline trace={trace} className="shrink-0" /> : <div className="flex h-16 w-60 shrink-0 items-center justify-center rounded bg-muted/50 text-xs text-muted-foreground">{trace ? I18n.get('rv2CaptureUnreadable') : I18n.get('rv2CaptureReading')}</div>}
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{value.fileName}</span>
        {trace && <span className={cn('text-xs', trace.error ? 'text-destructive' : 'text-muted-foreground')}>{traceSummary(trace)}</span>}
        {trace && !trace.error && (trace.xLabel || trace.yLabel) && (
          <span className="text-xs text-muted-foreground">{I18n.get('rv2TraceColumns', { x: trace.xLabel ?? input.xLabel ?? 'x', y: trace.yLabel ?? input.yLabel ?? 'y' })}</span>
        )}
      </div>
      {editable && (
        <button type="button" className="ml-auto flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label={I18n.get('rv2RemoveItem', { item: value.fileName })} onClick={() => onChange(null)}>
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
